import { useState, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Button,
  Heading,
  MultiStep,
  MultiStepModalProps,
  TextField,
  useAlert,
  useForm,
  useMultiStepModalControl,
} from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { SchemaFormValues, populateCreateSchema, createSubmitPayload } from './schema';
import { VoicemailBoxCreateRequest, VoicemailBoxCreateResponse } from './types';
import { useVoicemailBoxMutations } from './use-voicemail-box-mutations';
import { LocationsAndAccessCard, MiscellaneousCard } from './voicemail-box-detail';

const getDefaultState = (tenantId: string): VoicemailBoxCreateRequest => ({
  name: '',
  pin: undefined,
  locationIds: [],
  emailAddresses: [],
  smsNumbers: [],
  sendNotification: false,
  sharedAccess: false,
  playMessageDate: true,
  extensionNumber: undefined,
  tenantId,
});

export const CreateVoicemailBoxButton = ({
  tenantLocation,
  disabled = false,
}: {
  tenantLocation: SettingsTenantLocation;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { triggerProps, ...createVoicemailModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const handleOpenCreateVoicemailBox = () => {
    createVoicemailModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };
  return (
    <>
      <Button
        onClick={handleOpenCreateVoicemailBox}
        disabled={disabled}
        size='large'
        css={{ width: 'fit-content' }}
        trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'create' })}
      >
        {t('Create Voicemail Box')}
      </Button>
      <CreateModal tenantLocation={tenantLocation} {...createVoicemailModalProps} />
    </>
  );
};

type CreateVoicemailModalProps = {
  tenantLocation: SettingsTenantLocation;
  onSuccess?: (data: VoicemailBoxCreateResponse) => void;
} & MultiStepModalProps;

export const CreateModal = ({ tenantLocation, onSuccess, ...createVoicemailModalProps }: CreateVoicemailModalProps) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const onClose = createVoicemailModalProps.modalProps.onClose;
  const { navigate } = useSettingsNavigate();
  const [voicemailBox, _setVoicemailBox] = useState<VoicemailBoxCreateRequest>(() =>
    getDefaultState(tenantLocation.phoneTenantId ?? '')
  );
  const { create } = useVoicemailBoxMutations(tenantLocation.phoneTenantId ?? '');

  const populatedSchema = useMemo(() => {
    return populateCreateSchema(voicemailBox);
  }, [voicemailBox]);

  const form = useForm({
    fields: populatedSchema,
  });
  const textFieldProps = form.getFieldProps('name');
  const locationOptions = useMemo(() => {
    return (
      tenantLocation.children?.map((location) => ({
        label: location.name ?? '',
        value: location.locationId ?? '',
      })) ?? []
    );
  }, [tenantLocation]);

  const createVoicemailBox = (formValues: SchemaFormValues, tenantId: string) => {
    const finalState = createSubmitPayload({ tenantId, formValues });
    create.mutate(finalState, {
      onSuccess: (data) => {
        alerts.success({
          message: t('Voicemail Box created successfully.'),
          action: {
            label: t('Go to Voicemail Box'),
            onClick: () => navigate({ to: `/phone/voicemail-box/:id`, params: { id: data.mailbox.id } }),
          },
        });
        form.reset();
        onSuccess?.(data);
      },
      onError: () => {
        alerts.error('System failed to create Voicemail Box. Please try again.');
        logError('CreateModal:createMutation | Failed to create voicemail box', {
          data: { phoneTenantId: tenantLocation.phoneTenantId },
        });
      },
    });
    onClose();
  };

  const cancel = () => {
    form.reset();
    onClose();
  };

  const isUnify = tenantLocation.locationType === 'unify';

  return (
    <MultiStep.Modal minWidth={800} {...createVoicemailModalProps} onCancel={cancel}>
      {/* Step 1: Voicemail Box Name */}
      <MultiStep.Step
        id='name'
        header={{ title: t('Create Voicemail Box') }}
        label={t('Voicemail Box Name')}
        isValid={textFieldProps.value !== ''}
        footer={{ secondary: t('Cancel'), onClickSecondary: cancel }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Heading level={3}>{t('Voicemail Box Name')}</Heading>
          <div css={{ width: '318px', paddingLeft: theme.spacing(0.25) }}>
            <TextField
              {...textFieldProps}
              label={t('Voicemail Box Name')}
              name='name'
              data-trackingid={trackingId({
                context: 'setting',
                feature: 'voicemail-box',
                details: 'create::voicemail-name-input',
              })}
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 2: Location and Access */}
      <MultiStep.Step
        id='access'
        header={{ title: t('Create Voicemail Box') }}
        label={isUnify ? t('Locations and Access') : t('Access Details')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: 0,
            },
          }}
        >
          <Heading level={3}>{isUnify ? t('Locations and Access') : t('Access Details')}</Heading>
          <LocationsAndAccessCard
            form={form}
            locationOptions={locationOptions}
            tenantLocation={tenantLocation}
            canEditExtension
            canViewExtension
            useTextLabel
          />
        </div>
      </MultiStep.Step>

      {/* Step 3: Miscellaneous */}
      <MultiStep.Step
        id='miscellaneous'
        header={{ title: t('Create Voicemail Box') }}
        label={t('Miscellaneous')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
          onClickPrimary: () => createVoicemailBox(form.values, tenantLocation.phoneTenantId ?? ''),
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: 0,
            },
          }}
        >
          <Heading level={3}>{t('Miscellaneous')}</Heading>
          <MiscellaneousCard form={form} useTextLabel />
        </div>
      </MultiStep.Step>
    </MultiStep.Modal>
  );
};
